import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { DoBootstrap, inject, LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MomentDateAdapter } from '@angular/material-moment-adapter';

import LOCALE_BG from '@angular/common/locales/bg';
import LOCALE_DE from '@angular/common/locales/de';
import LOCALE_EN from '@angular/common/locales/en';
import LOCALE_ES from '@angular/common/locales/es';
import LOCALE_FR from '@angular/common/locales/fr';
import LOCALE_IT from '@angular/common/locales/it';
import LOCALE_PL from '@angular/common/locales/pl';
import LOCALE_BR from '@angular/common/locales/pt';
import LOCALE_RU from '@angular/common/locales/ru';

import { EffectsModule } from '@ngrx/effects';
import { Store, StoreModule } from '@ngrx/store';
import { HotkeyModule } from 'angular2-hotkeys';

import { SeComponentsModule } from '@se/components';
import {
    createTranslateLoader,
    CultureService,
    CustomPageToolbarResolver,
    ESTENSA_MODULE_TOKEN_SERVICE,
    EstensaServiceLocator,
    InMemoryTokenStore,
    LogService,
    RootEffects,
    rootReducerMap,
    RouterDataService,
    RouteToolbarResolver,
    seEnvironmentSet,
    SePlatformModule,
    TokenInterceptor,
} from '@se/platform';
import { UserControlResolver } from '@se/cp.engine';
import { UserControlService } from '@se/cp.usercontrol.resolver';
import { ConsoleLogDestination, Logger } from '@se/se.platform.base';

import { EmptyComponent } from '../empty/empty.component';
import { environment } from '../environments/environment';
import { HostComponent } from './host.component';
import { routes } from './host.routes';
import { HostHybridComponent } from './host-hybrid/host-hybrid.component';

import { HostEffects } from './host.effects';
import { extModules } from './build-specifics';
import { TooluploadModule } from '@se/modules.toolupload';
import { EcgViewerModule } from '@se/ecg.viewer.html5';

@NgModule({
    declarations: [HostComponent, HostHybridComponent, EmptyComponent],
    bootstrap: [],
    imports: [
        BrowserAnimationsModule,
        FormsModule,
        SePlatformModule.forHybrid(),
        SeComponentsModule,
        HotkeyModule.forRoot(),
        RouterModule.forRoot(routes, { useHash: true }),
        StoreModule.forRoot(
            {
                ...rootReducerMap,
            },
            {
                runtimeChecks: {
                    strictActionImmutability: environment.production === false,
                    strictActionSerializability: environment.production === false,
                    strictStateImmutability: environment.production === false,
                    strictStateSerializability: environment.production === false,
                },
            },
        ),
        extModules,
        EffectsModule.forRoot([RootEffects, HostEffects]),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: createTranslateLoader,
                deps: [HttpClient],
            },
        }),
        MatToolbarModule,
        MatIconModule,
        MatProgressSpinnerModule,
        TooluploadModule,
        EcgViewerModule
    ],
    providers: [
        provideAppInitializer(() => {
            const initializerFn = localeInitializer(inject(CultureService), inject(Logger));
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const initializerFn = configurationInitializer(inject(Store));
            return initializerFn();
        }),
        {
            provide: LOCALE_ID,
            deps: [CultureService],
            useFactory: localeFactory,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            deps: [InMemoryTokenStore],
            multi: true,
        },
        {
            provide: ESTENSA_MODULE_TOKEN_SERVICE,
            deps: [EstensaServiceLocator],
            useFactory: tokenServiceFactory,
        },
        {
            provide: Logger,
            deps: [LogService],
            useFactory: loggerFactory,
        },
        {
            provide: MAT_DATE_LOCALE,
            deps: [CultureService],
            useFactory: localeFactory,
        },
        {
            provide: DateAdapter,
            deps: [MAT_DATE_LOCALE],
            useClass: MomentDateAdapter,
        },
        {
            provide: CustomPageToolbarResolver,
            deps: [RouterDataService],
            useClass: RouteToolbarResolver,
        },
        {
            provide: UserControlResolver,
            useClass: UserControlService,
        },
    /*     StoreProvider, */
        provideHttpClient(withInterceptorsFromDi()),
    ],
})
export class HostModule implements DoBootstrap {
    // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method
    ngDoBootstrap() {}
}

export function localeFactory(cultureService: CultureService) {
    return cultureService.getCulture().substring(0, 2);
}

export function loggerFactory(logService: LogService) {
    const configuration = {
        destinations: [new ConsoleLogDestination(), logService],
        levels: environment.loggerConfiguration,
    };
    return new Logger(configuration);
}

export function tokenServiceFactory(estensaServiceLocator: EstensaServiceLocator) {
    return estensaServiceLocator.getService('TokenService');
}

export function configurationInitializer(store: Store) {
    return async () =>
        store.dispatch(
            seEnvironmentSet({
                env: {
                    isStandalone: false,
                    production: environment.production,
                },
            }),
        );
}

// https://github.com/angular/angular/issues/20487
export function localeInitializer(cultureService: CultureService, logger: Logger) {
    const SUPPORTED_LOCALES = {
        bg: LOCALE_BG,
        de: LOCALE_DE,
        en: LOCALE_EN,
        es: LOCALE_ES,
        fr: LOCALE_FR,
        it: LOCALE_IT,
        pl: LOCALE_PL,
        br: LOCALE_BR,
        ru: LOCALE_RU,
    };

    return async (): Promise<any> => {
        logger.debug('Initializing CultureService...');

        await cultureService.initializeAsync();

        let locale = 'en';
        try {
            locale = cultureService.getCulture().substring(0, 2);
        } catch (exception) {
            logger.warn(`The culture couldn't be detected. The default Angular locale is set to English`);
            logger.error(exception);
        }

        logger.debug('Initialized CultureService');
        logger.debug(`registerLocaleData ${locale}`);

        registerLocaleData(SUPPORTED_LOCALES[locale]);
    };
}
